<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const homeWorkspace = useHomeWorkspaceStore()

// ================================
// COMPUTED
// ================================
const getIsSearchContext = computed(() => {
    return !!(route.query.search ?? route.query.type)
})

const getSearchType = computed({
    get(): "building" | "folder" {
        const t = route.query.type as string
        return t === "folder" ? "folder" : "building"
    },
    set(v: "building" | "folder") {
        const q = { ...route.query } as Record<string, unknown>
        q.type = v
        if (v === "folder") {
            delete q.tags
        }
        router.replace({ query: q })
    },
})

const getTypeOptions = [
    { label: "Mes bâtiments", value: "building" as const },
    { label: "Mes dossiers", value: "folder" as const },
]

// ================================
// INTERACTIONS
// ================================
function onOpenExport(): void {
    homeWorkspace.openModal("exportAllData", {})
}

function onOpenBuildingOptions(): void {
    homeWorkspace.openModal("buildingOptions", {})
}

// ================================
// LIFECYCLE & WATCHERS
// ================================
onMounted(() => {
    if (authStore.isClientHomeys && !getIsSearchContext.value) {
        router.replace({ path: "/folder" })
    }
})

watch(
    () => authStore.isClientHomeys,
    (v) => {
        if (v && !getIsSearchContext.value) router.replace({ path: "/folder" })
    }
)
</script>

<template>
    <!-- ================================ -->
    <!-- PAGE — TOUS LES BÂTIMENTS -->
    <!-- ================================ -->
    <main
        v-if="!authStore.isClientHomeys || getIsSearchContext"
        class="page page-dashboard-buildings flex min-h-0 flex-1 flex-col gap-6 p-6"
        data-cy="page-dashboard-buildings"
    >
        <HomeWorkspaceDnDProvider>
            <header class="flex flex-wrap items-center justify-between gap-4">
                <h1 class="text-2xl font-semibold text-surface-800">
                    {{
                        getIsSearchContext
                            ? "Résultats de recherche"
                            : "Tous les bâtiments"
                    }}
                </h1>
                <div class="flex flex-wrap items-center gap-2">
                    <BaseButton
                        v-if="authStore.isEmployeWriteOrHigher"
                        label="Exporter les données"
                        severity="secondary"
                        outlined
                        @click="onOpenExport"
                    />
                    <BaseButton
                        v-if="authStore.isEmployeWriteOrHigher"
                        label="Ajouter des bâtiments"
                        @click="onOpenBuildingOptions"
                    />
                </div>
            </header>

            <div
                v-if="getIsSearchContext"
                class="flex flex-wrap items-center gap-4"
            >
                <BaseSelectButton
                    v-model="getSearchType"
                    :options="getTypeOptions"
                    option-label="label"
                    option-value="value"
                />
            </div>

            <HomeFolderList
                v-if="getIsSearchContext && getSearchType === 'folder'"
                search-mode
                :disabled-move="true"
                :disabled-remove="true"
            />

            <HomeBuildingList v-else variant="root" />

            <HomeBuildingDialogs />
            <HomeFolderDialogs />
            <HomeExportDialogs />
            <HomeBuildingActionsDialog />
        </HomeWorkspaceDnDProvider>
    </main>
</template>
